import { useCallback, useState } from 'react';
import { getSongs, SongSimple } from '@round/api';
import useAbortableEffect from '../../../../Hooks/useAbortableEffect';

type UseSongsParams = Pick<Parameters<typeof getSongs>[0], 'search' | 'page' | 'page_size'>;

export function useSongs({ page, search, page_size }: UseSongsParams) {
    const [songs, setSongs] = useState<SongSimple[]>([]);
    const [count, setCount] = useState(0);

    const [isInitialized, setIsInitialized] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [hasErrorLoading, setHasErrorLoading] = useState(false);

    const fetchData = useCallback(
        async (requestInit?: RequestInit) => {
            try {
                setIsLoading(true);
                setHasErrorLoading(false);
                const response = await getSongs({ page, page_size, search }, requestInit);
                setSongs(response.data.results);
                setCount(response.data.count);
                setIsInitialized(true);
            } catch (e) {
                if (e instanceof Error && e.name === 'AbortError') {
                    return;
                }

                setHasErrorLoading(true);
            } finally {
                setIsLoading(false);
            }
        },
        [page, page_size, search]
    );

    useAbortableEffect(
        (signal) => {
            fetchData({ signal });
        },
        [fetchData]
    );

    return {
        songs,
        count,
        isInitialized,
        isLoading,
        hasErrorLoading,
        refresh: fetchData,
        updateSong: (song: SongSimple) => setSongs((songs) => songs.map((s) => (s.id === song.id ? song : s))),
    };
}
